import type { FieldPathSegments } from './field-path';
import { R4_DATATYPES, R4_SCHEMA } from './r4-schema.generated';

/**
 * The structural authority: what the FHIR spec says lives at a path.
 * Backed by the generated R4 manifest (147 resources + 41 complex
 * datatypes from @medplum/definitions). This is the runtime side of the
 * schema-driven direction — the suffix-table and shape-sniffing stopgaps
 * converge here over time.
 *
 * Pure module: no React, no data-stack contact.
 */

export type ResolvedSchemaElement = {
  /** Terminal FHIR type: 'string', 'date', 'HumanName', 'Quantity', … */
  datatype: string;
  isArray: boolean;
  /** Set when the terminal segment was a choice instance key (valueQuantity → value[x]). */
  choiceOf?: string;
};

const PRIMITIVE_TYPES = new Set([
  'base64Binary',
  'boolean',
  'canonical',
  'code',
  'date',
  'dateTime',
  'decimal',
  'id',
  'instant',
  'integer',
  'markdown',
  'oid',
  'positiveInt',
  'string',
  'time',
  'unsignedInt',
  'uri',
  'url',
  'uuid',
  'xhtml',
]);

export function isPrimitiveDatatype(type: string): boolean {
  return PRIMITIVE_TYPES.has(type);
}

/** `valueQuantity` against a `value[x]` choice → 'Quantity'; undefined if no match. */
function resolveChoiceKey(
  record: Readonly<Record<string, { t: string; a?: 1; c?: readonly string[] }>>,
  prefix: string,
  segment: string,
): { element: ResolvedSchemaElement; base: string } | undefined {
  for (const [key, element] of Object.entries(record)) {
    if (element.t !== 'choice' || !element.c) continue;
    const base = prefix ? key.replace(`${prefix}.`, '') : key;
    if (key !== (prefix ? `${prefix}.${base}` : base)) continue;
    if (!segment.startsWith(base) || segment === base) continue;
    const suffix = segment.slice(base.length);
    const match = element.c.find(
      (type) =>
        type === suffix ||
        type === suffix.charAt(0).toLowerCase() + suffix.slice(1),
    );
    if (match) {
      return {
        element: { datatype: match, isArray: element.a === 1, choiceOf: base },
        base,
      };
    }
  }
  return undefined;
}

/**
 * Resolve what the spec says lives at `segments` under `rootType`.
 * Undefined = the path does not exist in R4 (typo, wrong branch, or
 * navigation past a primitive) — the type layer should have caught it;
 * callers treat it as a dev error, never a crash.
 */
export function schemaElementAt(
  rootType: string,
  segments: FieldPathSegments,
): ResolvedSchemaElement | undefined {
  let record = R4_SCHEMA[rootType] ?? R4_DATATYPES[rootType];
  if (!record) return undefined;
  let prefix = '';
  let current: ResolvedSchemaElement | undefined;

  for (const segment of segments) {
    if (typeof segment !== 'string') {
      // Index / filter: selects one item — only meaningful on an array.
      if (!current?.isArray) return undefined;
      current = { ...current, isArray: false };
      continue;
    }

    // Descend into the previous element before looking the segment up.
    if (current) {
      if (isPrimitiveDatatype(current.datatype)) return undefined;
      if (current.datatype === 'BackboneElement') {
        // Backbone children live in the same record under a longer key —
        // prefix was already extended when the backbone matched.
      } else {
        const datatypeRecord = R4_DATATYPES[current.datatype];
        if (!datatypeRecord) return undefined; // Resource, Extension, unknown
        record = datatypeRecord;
        prefix = '';
      }
    }

    const key = prefix ? `${prefix}.${segment}` : segment;
    const element = record[key];
    if (element && element.t !== 'choice') {
      current = { datatype: element.t, isArray: element.a === 1 };
      if (element.t === 'BackboneElement') prefix = key;
      continue;
    }
    if (element && element.t === 'choice') {
      // Bare choice base used as a segment — ambiguous; not supported.
      return undefined;
    }
    const choice = resolveChoiceKey(record, prefix, segment);
    if (!choice) return undefined;
    current = choice.element;
    if (choice.element.datatype === 'BackboneElement') prefix = key;
  }
  return current;
}

export type PathEvaluation = {
  value: unknown;
  /** Object holding the terminal element — where its `_<element>` DAR sibling lives. */
  owner: object | undefined;
  /** The terminal element key on `owner`. */
  terminalKey: string | undefined;
};

function matchesFilter(
  item: unknown,
  filter: Readonly<Record<string, unknown>>,
): boolean {
  if (item === null || typeof item !== 'object') return false;
  return Object.entries(filter).every(([key, expected]) => {
    const actual = (item as Record<string, unknown>)[key];
    if (expected !== null && typeof expected === 'object') {
      const candidates = Array.isArray(actual) ? actual : [actual];
      return candidates.some((candidate) =>
        matchesFilter(candidate, expected as Record<string, unknown>),
      );
    }
    return actual === expected;
  });
}

/**
 * Extract the value at `segments`, following the same walk the schema
 * lookup validates. Arrays auto-select their first item when navigated
 * without an index/filter. Missing anywhere → undefined value (the field
 * resolvers decide what that absence means).
 */
export function evaluateFieldPath(
  root: object,
  segments: FieldPathSegments,
): PathEvaluation {
  let value: unknown = root;
  let owner: object | undefined;
  let terminalKey: string | undefined;

  for (const segment of segments) {
    if (value === null || value === undefined) {
      return { value: undefined, owner, terminalKey };
    }
    if (typeof segment === 'string') {
      if (Array.isArray(value)) value = value[0];
      if (value === null || typeof value !== 'object') {
        return { value: undefined, owner, terminalKey };
      }
      owner = value;
      terminalKey = segment;
      value = (value as Record<string, unknown>)[segment];
    } else if (typeof segment === 'number') {
      value = Array.isArray(value) ? value[segment] : undefined;
    } else {
      value = Array.isArray(value)
        ? value.find((item) => matchesFilter(item, segment))
        : undefined;
    }
  }
  // GraphQL "selected but absent" — null means the field does not exist.
  if (value === null) value = undefined;
  return { value, owner, terminalKey };
}

/** Dotted policy path from the segments: strings only, filters/indexes dropped. */
export function fieldPathString(segments: FieldPathSegments): string {
  return segments.filter((s): s is string => typeof s === 'string').join('.');
}
